import { useMemo, useEffect, useCallback, type MouseEvent } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Helmet } from 'react-helmet-async';
import { Formik, Form, Field, useFormikContext, type FieldProps, type FieldMetaProps } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, LinearProgress, CardContent, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';

import { User, type UserProps } from '../models';
import { useGetUserByEmailQuery, useUpdateUserMutation } from '../slices/apis/userApi';

const UserEditForm = ({
	isSuccess,
	isError,
}: {
	isSuccess?: boolean;
	isError?: boolean;
}) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { isSubmitting } = useFormikContext();

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	useEffect(() => {
		if (isSuccess) {
			navigate('/dashboard/requests');
		}
	}, [isSuccess, navigate]);

	return (
		<Form>
			<Grid container spacing={2}>
				<Grid size={{ md: 12 }}>
					<Field name="firstname">
						{({
							field,
							meta,
						}: {
							field: FieldProps<UserProps['firstname']>;
							meta: FieldMetaProps<UserProps['firstname']>;
						}) => (
							<TextField
								{...field}
								fullWidth
								margin="dense"
								label="First Name"
								variant="standard"
								error={meta.touched && Boolean(meta.error)}
								helperText={meta.touched && meta.error}
							/>
						)}
					</Field>
				</Grid>
				<Grid size={{ md: 12 }}>
					<Field name="lastname">
						{({
							field,
							meta,
						}: {
							field: FieldProps<UserProps['lastname']>;
							meta: FieldMetaProps<UserProps['lastname']>;
						}) => (
							<TextField
								{...field}
								fullWidth
								margin="dense"
								label="Last Name"
								variant="standard"
								error={meta.touched && Boolean(meta.error)}
								helperText={meta.touched && meta.error}
							/>
						)}
					</Field>
				</Grid>
				<Grid size={{ md: 12 }}>
					<Field name="phone">
						{({
							field,
							meta,
						}: {
							field: FieldProps<UserProps['phone']>;
							meta: FieldMetaProps<UserProps['phone']>;
						}) => (
							<TextField
								{...field}
								fullWidth
								margin="dense"
								label="Phone"
								variant="standard"
								inputProps={{ inputMode: 'numeric', maxLength: 10 }}
								error={meta.touched && Boolean(meta.error)}
								helperText={meta.touched && meta.error}
							/>
						)}
					</Field>
				</Grid>
				<Grid size={{ md: 12 }}>
					<Field name="company">
						{({
							field,
							meta,
						}: {
							field: FieldProps<UserProps['company']>;
							meta: FieldMetaProps<UserProps['company']>;
						}) => (
							<TextField
								{...field}
								fullWidth
								margin="dense"
								label="Company"
								variant="standard"
								error={meta.touched && Boolean(meta.error)}
								helperText={meta.touched && meta.error}
							/>
						)}
					</Field>
				</Grid>
				<Grid size={{ md: 4 }}>
					<Grid container justifyContent="start" spacing={4}>
						<Grid size={{ md: 6 }}>
							<Button loading={isSubmitting} variant="outlined" color="primary" type="submit">
								<span>Update</span>
							</Button>
						</Grid>
						<Grid size={{ md: 6 }}>
							<Button
								color="error"
								variant="outlined"
								disabled={isSubmitting}
								data-route="/dashboard/requests"
								onClick={handleNavigate}
								sx={{
									'&.Mui-disabled': {
										color: theme.palette.error.light,
										borderColor: theme.palette.error.light,
									},
								}}
							>
								Cancel
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Form>
	);
};

export default function UserEdit() {
	const { user: auth0User, isLoading: isLoadingAuth0User } = useAuth0();
	const { data: user, isLoading: isLoadingUser } = useGetUserByEmailQuery(auth0User?.email);
	const [updateUser, { isSuccess, isError }] = useUpdateUserMutation();

	const initialValues = useMemo(() => {
		if (user) {
			return User.schema.cast({
				...user,
			});
		}

		return User.schema.getDefault();
	}, [user]);

	if (isLoadingAuth0User || isLoadingUser) {
		return <LinearProgress />;
	}

	return (
		<>
			<Helmet>
				<title> Edit User - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Box sx={{ p: 2 }}>
				<Grid container justifyContent="start">
					<Grid size={{ md: 6 }}>
						<Typography variant="h4" gutterBottom>
							Edit User {auth0User?.email}
						</Typography>
						<Card>
							<CardContent>
								<Formik
									initialValues={initialValues}
									enableReinitialize
									validationSchema={User.schema}
									onSubmit={async (values) => {
										await updateUser(User.normalizeForUpdate(values));
									}}
								>
									<UserEditForm isSuccess={isSuccess} isError={isError} />
								</Formik>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
		</>
	);
}
